import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import { NumericTextField } from '@/components';
import { useConfigStore } from '@/stores/useConfigStore';
import { useUIStore } from '@/stores/useUIStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useInputStore } from '@/stores/useInputStore';
import { MATERIAL_OPTIONS } from '@/constants/feaConstants';
import type { MaterialType, ConfigState } from '@/types/config';

/* ── Section heading (matches Python dialog styling) ───────── */
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, fontSize: '0.875rem' }}
  >
    {children}
  </Typography>
);

/* ── Material Select (reusable for core / outer / inter-sleeve) */
interface MaterialSelectProps {
  label: string;
  value: MaterialType;
  onChange: (v: MaterialType) => void;
  disabled?: boolean;
}

const MaterialSelect: React.FC<MaterialSelectProps> = ({
  label, value, onChange, disabled,
}) => (
  <FormControl size="small" fullWidth disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select
      value={value}
      label={label}
      onChange={(e: SelectChangeEvent) => onChange(e.target.value as MaterialType)}
    >
      {MATERIAL_OPTIONS.map((opt) => (
        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

/* ── Priority Select (one per stiffness direction) ─────────── */
const REFERENCE_VALUE = 0;

interface PrioritySelectProps {
  label: string;
  value: number;
  options: number[];
  usedValues: number[];
  onChange: (v: number) => void;
}

const PrioritySelect: React.FC<PrioritySelectProps> = ({
  label, value, options, usedValues, onChange,
}) => (
  <FormControl size="small" fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select
      value={String(value)}
      label={label}
      onChange={(e: SelectChangeEvent) => onChange(Number(e.target.value))}
    >
      <MenuItem value={String(REFERENCE_VALUE)}>Reference</MenuItem>
      {options.map((opt) => (
        <MenuItem
          key={opt}
          value={String(opt)}
          disabled={opt !== value && usedValues.includes(opt)}
        >
          {`Priority ${opt}`}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/* ──────────────────────────────────────────────────────────── */
/*  ConfigDialog                                                */
/* ──────────────────────────────────────────────────────────── */
const ConfigDialog: React.FC = () => {
  const open = useUIStore((s) => s.configDialogOpen);
  const closeDialog = useUIStore((s) => s.closeDialog);

  /* Config store selectors */
  const coreMaterial = useConfigStore((s) => s.coreMaterial);
  const outerMaterial = useConfigStore((s) => s.outerMaterial);
  const interSleeveMaterial = useConfigStore((s) => s.interSleeveMaterial);
  const weightPockets = useConfigStore((s) => s.weightPockets);
  const stiffnessPriorities = useConfigStore((s) => s.stiffnessPriorities);
  const clampLoad = useConfigStore((s) => s.clampLoad);
  const allowableYield = useConfigStore((s) => s.allowableYield);
  const coreIdCalculator = useConfigStore((s) => s.coreIdCalculator);
  const sealRadial = useConfigStore((s) => s.sealRadial);
  const minContactArea = useConfigStore((s) => s.minContactArea);

  const setCoreMaterial = useConfigStore((s) => s.setCoreMaterial);
  const setOuterMaterial = useConfigStore((s) => s.setOuterMaterial);
  const setInterSleeveMaterial = useConfigStore((s) => s.setInterSleeveMaterial);
  const setWeightPockets = useConfigStore((s) => s.setWeightPockets);
  const setStiffnessPriorities = useConfigStore((s) => s.setStiffnessPriorities);
  const setClampLoad = useConfigStore((s) => s.setClampLoad);
  const setAllowableYield = useConfigStore((s) => s.setAllowableYield);
  const setCoreIdCalculator = useConfigStore((s) => s.setCoreIdCalculator);
  const setSealRadial = useConfigStore((s) => s.setSealRadial);
  const setMinContactArea = useConfigStore((s) => s.setMinContactArea);
  const saveSnapshot = useConfigStore((s) => s.saveSnapshot);

  /* Bushing type for dynamic priority list */
  const bushingType = useBushingStore((s) => s.bushingType);
  const config = useBushingStore((s) => s.config);

  const isB1 = bushingType === 'B1';
  const isSteelCore = coreMaterial === 'Steel';

  // Default sealing value for Aluminum
  const DEFAULT_SEAL_RADIAL = 0.6;

  /* Snapshot on open so we can restore on cancel */
  const snapshotRef = useRef<Omit<ConfigState, 'lastSavedConfig'> | null>(null);

  useEffect(() => {
    if (open) {
      const s = useConfigStore.getState();
      snapshotRef.current = {
        coreMaterial: s.coreMaterial,
        outerMaterial: s.outerMaterial,
        interSleeveMaterial: s.interSleeveMaterial,
        weightPockets: s.weightPockets,
        stiffnessPriorities: { ...s.stiffnessPriorities },
        clampLoad: s.clampLoad,
        allowableYield: s.allowableYield,
        coreIdCalculator: s.coreIdCalculator,
        sealRadial: s.sealRadial,
        minContactArea: s.minContactArea,
      };
    }
  }, [open]);

  /* When core material changes to Steel, disable weight pockets and zero seal radial */
  useEffect(() => {
    if (isSteelCore) {
      setWeightPockets(false);
      setSealRadial(0);
    }
    // When switching from Steel to Aluminum, reset sealRadial to default
    else {
      setSealRadial(DEFAULT_SEAL_RADIAL);
    }
  }, [isSteelCore, setWeightPockets, setSealRadial]);

  /* Auto-populate Core ID from Packaging section's Core ID */
  const packagingCoreId = useInputStore((s) => s.packaging.coreId);
  useEffect(() => {
    const current = useConfigStore.getState().coreIdCalculator;
    if (packagingCoreId !== current) {
      setCoreIdCalculator(packagingCoreId);
    }
  }, [packagingCoreId, setCoreIdCalculator]);

  const setPackaging = useInputStore((s) => s.setPackaging);
  // Keep packaging coreId in sync with config dialog coreIdCalculator
  useEffect(() => {
    const current = useInputStore.getState().packaging.coreId;
    if (coreIdCalculator !== current) {
      setPackaging({ coreId: coreIdCalculator });
    }
  }, [coreIdCalculator, setPackaging]);

  /* Build priority options from param names */
  const paramNames = config.paramNames;
  const priorityOptions = useMemo(
    () => Array.from({ length: paramNames.length }, (_, i) => i + 1),
    [paramNames.length],
  );

  /* Initialise priorities when they don't match current bushing type */
  useEffect(() => {
    const currentKeys = Object.keys(stiffnessPriorities);
    const needsInit =
      currentKeys.length !== paramNames.length ||
      !paramNames.every((p) => currentKeys.includes(p));

    if (needsInit) {
      const init: Record<string, number> = {};
      paramNames.forEach((p) => { init[p] = REFERENCE_VALUE; });
      setStiffnessPriorities(init);
    }
  }, [paramNames, stiffnessPriorities, setStiffnessPriorities]);

  const usedPriorityValues = useMemo(
    () => Object.values(stiffnessPriorities).filter((v) => v !== REFERENCE_VALUE),
    [stiffnessPriorities],
  );

  /* Build a map from param key → display label */
  const displayLabels = config.displayLabels as Record<string, string>;

  /* ── Handlers ─────────────────────────────────────────────── */
  const handlePriorityChange = useCallback(
    (param: string, val: number) => {
      setStiffnessPriorities({ ...stiffnessPriorities, [param]: val });
    },
    [stiffnessPriorities, setStiffnessPriorities],
  );

  const handleConfirm = useCallback(() => {
    const wasDirty = useConfigStore.getState().dirty;
    saveSnapshot();
    if (wasDirty) {
      useInputStore.getState().markDirty();
    }
    closeDialog('configDialog');
  }, [saveSnapshot, closeDialog]);

  const handleCancel = useCallback(() => {
    /* Restore snapshot */
    if (snapshotRef.current) {
      const snap = snapshotRef.current;
      setCoreMaterial(snap.coreMaterial);
      setOuterMaterial(snap.outerMaterial);
      setInterSleeveMaterial(snap.interSleeveMaterial);
      setWeightPockets(snap.weightPockets);
      setStiffnessPriorities(snap.stiffnessPriorities);
      setClampLoad(snap.clampLoad);
      setAllowableYield(snap.allowableYield);
      setCoreIdCalculator(snap.coreIdCalculator);
      setSealRadial(snap.sealRadial);
      setMinContactArea(snap.minContactArea);
    }
    closeDialog('configDialog');
  }, [
    closeDialog, setCoreMaterial, setOuterMaterial, setInterSleeveMaterial,
    setWeightPockets, setStiffnessPriorities, setClampLoad, setAllowableYield,
    setCoreIdCalculator, setSealRadial, setMinContactArea,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
        Configuration
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {/* ── Material Section ──────────────────────────────── */}
        <SectionTitle>Material</SectionTitle>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <MaterialSelect
            label="Core material"
            value={coreMaterial}
            onChange={setCoreMaterial}
          />
          <MaterialSelect
            label="Outer material"
            value={outerMaterial}
            onChange={setOuterMaterial}
          />
          {isB1 && (
            <MaterialSelect
              label="Inter sleeve material"
              value={interSleeveMaterial}
              onChange={setInterSleeveMaterial}
            />
          )}
        </Box>

        <Divider />

        {/* ── Min. core OD Section ─────────────────────────── */}
        <SectionTitle>Min. core OD</SectionTitle>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <NumericTextField
            label="Clamp load [kN]"
            value={clampLoad}
            onChange={setClampLoad}
            size="small"
            fullWidth
          />
          <NumericTextField
            label="Allowable yield [MPa]"
            value={allowableYield}
            onChange={setAllowableYield}
            size="small"
            fullWidth
          />
          <NumericTextField
            label="Core ID [mm]"
            value={coreIdCalculator}
            onChange={setCoreIdCalculator}
            size="small"
            fullWidth
          />
          <NumericTextField
            label="Sealing size radially [mm]"
            value={sealRadial}
            onChange={setSealRadial}
            disabled={isSteelCore}
            size="small"
            fullWidth
          />
          <NumericTextField
            label="Min contact area [mm²] (optional)"
            value={minContactArea}
            onChange={setMinContactArea}
            size="small"
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={weightPockets}
                onChange={(_, checked) => setWeightPockets(checked)}
                disabled={isSteelCore}
                size="small"
                color="info"
              />
            }
            label="Weight Pocket"
            sx={{ ml: 0 }}
          />
        </Box>

        <Divider />

        {/* ── Target Stiffness Priority Section ────────────── */}
        <SectionTitle>Target Stiffness Priority</SectionTitle>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {paramNames.map((param) => (
            <PrioritySelect
              key={param}
              label={displayLabels[param] ?? param}
              value={stiffnessPriorities[param] ?? REFERENCE_VALUE}
              options={priorityOptions}
              usedValues={usedPriorityValues}
              onChange={(v) => handlePriorityChange(param, v)}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button 
        variant="contained"
        onClick={handleCancel} 
        sx={{ textTransform: 'none',
          bgcolor: 'info.main',
          boxShadow: 4, 
          '&:hover': { bgcolor: 'info.dark', boxShadow: 4 },
         }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          sx={{ textTransform: 'none',
            bgcolor: 'info.main',
            boxShadow: 4, 
            '&:hover': { bgcolor: 'info.dark', boxShadow: 4 },
          }}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ConfigDialog);
